import { h, Fragment } from "preact";
import Router, { RoutableProps } from "preact-router";
import { useEffect, useState } from "preact/hooks";
import { getTemplateLabels, Label } from "../../../api/product-api";
import { baseUrl, route } from "../../../App";
import { Snackbar, SnackbarShowCommand } from "../../../components/Snackbar";
import { useCommand } from "../../../hooks";
import { parseToDateAndFormatIfExists } from "../../../utils";
import { SelectableList, SelectableListItem } from "../../../components/SelectableList";
import { LabelTemplateEditPage } from "./edit";
import { generateDownloadZip, generateLabelInfo } from "../utils";

import "./styles.scss";
import { LabelSVGInteractive } from "../LabelSVGInteractive";
import { LabelMetadataElementList } from "../LabelMetadataElementList";

export function LabelTemplatePage(props: RoutableProps) {
    return <Router>
        <LabelTemplateHomePage path={`${baseUrl}/label/template`}/>
        <LabelTemplateEditPage path={`${baseUrl}/label/template/:id`}/>
        <LabelTemplateEditPage path={`${baseUrl}/label/template/new`}/>
    </Router>
}

const knownElementProperties = {
    maxCharacters: "Maksymalna ilość znaków"
} as { [key: string]: string }

function LabelTemplateElementPropertiesTable(props: { metadata: { [key: string]: string }, element: string}) {
    const params = props.metadata["properties-" + props.element] ? new URLSearchParams(props.metadata["properties-" + props.element]) : null;

    return params ? <div class="template-info-table">
        <div style="font-weight: bold">Nazwa</div>
        <div style="font-weight: bold">Wartość</div>
        <div>Typ</div>
        <div>{props.element.split("-")[1]}</div>
        {Array.from(params.entries()).map(([k, v]) => [
            <div>{knownElementProperties[k] ?? k}</div>,
            <div>{v}</div>
        ])}
    </div> : <div>Nie wybrano elementu</div>;
}

function LabelTemplateElementPreview(props: { label: Label }) {
    const [selectedElement, setSelectedElement] = useState("")
    const metadata = props.label.metadata?.metadata ?? {};

    return <div style="display: flex; align-items: stretch;">
        <div
            class="paper-2"
            style="display: flex; align-items: flex-start; padding: 10px;"
            onClick={(e) => e.currentTarget === e.target && setSelectedElement("")}
        >
            <LabelMetadataElementList
                style="margin-right: 10px;"
                header="Elementy zmienne"
                emptyListMessage="Brak elementów zmiennych"
                metadata={metadata}
                onChange={setSelectedElement}
                value={selectedElement}
                showOnlyDescriptions
            />
            <LabelSVGInteractive
                path={props.label.path}
                highlight={selectedElement.split("-").slice(2).join("-")}
                maxWidth={500}
                maxHeight={200}
            />
            <div style="flex: 1 0; margin-left: 10px;" class="template-info">
                <h2>Właściwości elementu</h2>
                <LabelTemplateElementPropertiesTable metadata={metadata} element={selectedElement}/>
            </div>
        </div>
        <div style="flex: 1 0; margin-left: 10px;" class="paper-2 template-info">
            <h2>Informacje o szablonie</h2>
            {metadata ? <div class="template-info-table">
                { metadata["printer"] ? [
                    <div>Drukarka</div>,
                    <div>{metadata["printer"]}</div>,
                ] : null}
                <div>Rewizja</div>
                <div>{props.label.revision}</div>
                { metadata["tape-width"] ? [
                    <div>Szerokość taśmy</div>,
                    <div>{metadata["tape-width"]}</div>,
                ] : null}
                { metadata["label-length"] ? [
                    <div>Długość naklejki</div>,
                    <div>{metadata["label-length"]}</div>,
                ] : null}
                { metadata["tape-type"] ? [
                    <div>Rodzaj taśmy</div>,
                    <div>{metadata["tape-type"]}</div>,
                ] : null }
                <div>Ilość zmiennych elementów</div>
                <div>{Object.keys(metadata).filter(v => v.startsWith("element-")).length}</div>
                { metadata["gs1-gtin"] ? [
                    <div>GTIN</div>,
                    <div>{metadata["gs1-gtin"]}</div>,
                ] : null }
                { metadata["gs1-gtin13"] ? [
                    <div>GTIN</div>,
                    <div>{metadata["gs1-gtin13"]}</div>,
                ] : null }
            </div>: null }
        </div>
    </div>;
}

function LabelTemplateItem(props: { item: Label }) {
    const [expanded, setExpanded] = useState(false);
    const item = props.item;

    return <SelectableListItem item={item}>
        {({ isSelected, select }) => <>
            <tr class={"item" + (isSelected(item) ? " selected" : "")} onClick={(e) => {
                select(item, e.ctrlKey, e.shiftKey);
            }}>
                <td onClick={(e) => {
                    select(item, e.ctrlKey, e.shiftKey);
                    e.preventDefault();
                    e.stopPropagation();
                }}><input type="checkbox" class="tooltip" data-tooltip="Zaznacz/Odznacz" checked={isSelected(item)}/></td>
                <td>{item.id}</td>
                <td>{item.name}</td>
                <td>{item.revision}</td>
                <td>{item.path}</td>
                <td>{item.creator.email}</td>
                <td>{parseToDateAndFormatIfExists(item.createdAt)}</td>
                <td style="text-align: center; white-space: nowrap">
                    <button type="button" data-tooltip="Sklonuj..." class="icon borderless flat" onClick={() => route(`/label/template/new?cloneId=${item.id}`)}>content_copy</button>
                    <button type="button" data-tooltip="Edytuj..." class="icon borderless flat" onClick={() => route(`/label/template/${item.id}`)}>edit</button>
                    <button type="button" data-tooltip="Rozwiń szczegóły..." class="icon borderless flat" onClick={(e) => {
                        setExpanded(!expanded);
                        e.preventDefault();
                        e.stopPropagation();
                    }}>{expanded ? "expand_less" : "expand_more"}</button>
                </td>
            </tr>
            {expanded ? <tr>
                <td colSpan={9} style="padding: 10px">
                    <LabelTemplateElementPreview label={item}/>
                </td>
            </tr> : null}
        </>}
    </SelectableListItem>
}

export function LabelTemplateHomePage(props: RoutableProps & { successMessage?: string }) {
    const [labels, setLabels] = useState(null as null | Label[]);
    const [filteredLabels, setFilteredLabels] = useState([] as Label[]);
    const [actionProgress, setActionProgress] = useState(null as null | number);
    const [labelsSearch, setLabelsSearch] = useState( localStorage.getItem("label-metadata-search") ?? "");
    const [page, setPage] = useState(0);
    const [count, setCount] = useState(100);
    const [selectedLabels, setSelectedLabels] = useState([] as readonly Label[]);
    const [lastSelectedLabelIndex, setLastSelectedLabelIndex] = useState(null as number | null);
    const showSnackbarCommand = useCommand<SnackbarShowCommand>();

    useEffect(() => {
        const message = props.successMessage;

        if (!message) {
            return;
        }

        setTimeout(() => showSnackbarCommand.run(message, "success", "check", 4000), 100);
        route("/label/template");
    }, [props.successMessage]);

    useEffect(() => {
        getTemplateLabels().then(async (labels) => {
            setLabels(labels);
            setFilteredLabels(labels);
        });
    }, [page, count]);

    useEffect(() => {
        setLastSelectedLabelIndex(null);
    }, [filteredLabels]);

    useEffect(() => {
        if (labels) {
            setFilteredLabels(
                labels.filter(item => !labelsSearch
                    || item.name.toLowerCase().indexOf(labelsSearch.toLowerCase()) >= 0
                    || item.path.toLowerCase().indexOf(labelsSearch.toLowerCase()) >= 0
                    || item.creator.email.toLowerCase().indexOf(labelsSearch.toLowerCase()) >= 0
                    || parseToDateAndFormatIfExists(item.createdAt).toLocaleString().indexOf(labelsSearch) >= 0)
            );
        }
        else {
            setFilteredLabels([]);
        }
    }, [labels, labelsSearch])

    if (!labels) {
        return <div style="display: flex; flex-direction: column; justify-content: center; align-items: center; width: 100%; height: 100%">
            <div>Pobieranie danych...</div>
            <div class="loader"></div>
        </div>;
    }

    return <div class="label-template-page">
        <Snackbar doShow={showSnackbarCommand}/>
        {labels ? [
            <h1>Szablony naklejek</h1>,
            <div class="order-items paper">
                <div class="search">
                    <input class="search-text" placeholder="Wyszukaj..." value={labelsSearch} onInput={(e) => {
                        setLabelsSearch(e.currentTarget.value);
                        localStorage.setItem("label-metadata-search", e.currentTarget.value);
                    }}/>
                    <button type="button" onClick={() => route("/label/template/new")} style="margin-left: 10px;">Utwórz szablon naklejki</button>
                </div>
                {/* <div>
                    <button type="button" class={page <= 0 ? "disabled" : ""} onClick={() => setPage(page - 1)}>Poprzednia strona</button>
                    <button type="button" onClick={() => setPage(page + 1)}>Następna strona</button>
                    <input type="number" value={count} onChange={(e) => setCount(+e.currentTarget.value)}/>
                </div> */}
                <div class="actions" style="margin-bottom: 10px; display: flex; align-items: center">
                    {selectedLabels.length > 0 ?  [
                        <button type="button" class="icon flat" data-tooltip="Odznacz wszystkie" onClick={() => {
                            setSelectedLabels([]);
                        }}>remove</button>,
                        <button type="button" style="margin-left: 10px;" data-tooltip="Pobierz w formacie PNG" onClick={async () => {
                            await generateDownloadZip(selectedLabels, (v) => setActionProgress(v), "png");
                        }}>PNG</button>,
                        <button type="button" style="margin-left: 10px;" data-tooltip="Pobierz w formacie SVG" onClick={async () => {
                            await generateDownloadZip(selectedLabels, (v) => setActionProgress(v), "svg");
                        }}>SVG</button>,
                        <button type="button" style="margin-left: 10px;" data-tooltip="Pobierz w formacie PDF" onClick={async () => {
                            await generateDownloadZip(selectedLabels, (v) => setActionProgress(v), "pdf-separate");
                        }}>PDF</button>,
                        <button type="button" style="margin-left: 10px;" data-tooltip="Pobierz w formacie PDF scalone w jednym pliku" onClick={async () => {
                            await generateDownloadZip(selectedLabels, (v) => setActionProgress(v), "pdf");
                        }}>Zbiorczy PDF</button>
                    ]: [
                        <button type="button" class="icon flat" data-tooltip="Zaznacz wszystkie" onClick={() => {
                            setSelectedLabels(filteredLabels);
                        }}>check_box_outline_blank</button>,
                    ]}
                    <div style="flex: 1 1;"/>
                    {actionProgress !== null ? <progress min={0} max={100} value={actionProgress * 100}/> : null}
                </div>

                <SelectableList items={filteredLabels} value={selectedLabels} onChange={(items) => {
                    setSelectedLabels(items);
                }}>
                    <table class="labels list secondary" style="margin-bottom: 10px; user-select: none">
                        <thead>
                            <tr>
                                <th class="header"></th>
                                <th class="header">Id</th>
                                <th class="header">Nazwa</th>
                                <th class="header">Rewizja</th>
                                <th class="header">Ścieżka</th>
                                <th class="header">Rejestrujący</th>
                                <th class="header">Data rejestracji</th>
                                <th class="header">Akcje</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredLabels.map((item) =>
                                <LabelTemplateItem item={item}/>
                            )}
                        </tbody>
                    </table>
                </SelectableList>
            </div>
        ] : null}
    </div>;
}
